import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
} from '@mui/material';
import {
  Delete,
  Warning,
  Close,
  CheckCircle,
} from '@mui/icons-material';

interface OrderDeleteModalProps {
  open: boolean;
  onClose: () => void;
  order: any;
  onDelete?: (deletedItems: string[]) => void;
}

const OrderDeleteModal: React.FC<OrderDeleteModalProps> = ({ 
  open, 
  onClose, 
  order, 
  onDelete 
}) => {
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const items = order?.items || [];

  // Reset selections when modal opens
  React.useEffect(() => {
    if (open) {
      setSelectedItems([]);
      setShowConfirmation(false);
    }
  }, [open]);

  const handleItemToggle = (itemId: string) => {
    setSelectedItems(prev => 
      prev.includes(itemId) 
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  };

  const handleSelectAll = () => {
    if (selectedItems.length === items.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(items.map((item: any) => item.itemId));
    }
  };

  const handleDeleteClick = () => {
    if (selectedItems.length === 0) {
      return;
    }
    setShowConfirmation(true);
  };

  const handleConfirmDelete = async () => {
    setIsDeleting(true);
    try {
      // Here you would call your delete API
      // await deleteOrderItems(selectedItems);
      
      onDelete?.(selectedItems);
      onClose();
    } catch (error) {
      console.error('Error deleting items:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const formatPrice = (price: number) => {
    return typeof price === 'number' ? `JOD${price.toFixed(2)}` : 'JOD0.00';
  };

  const calculateItemTotal = (item: any) => {
    // Prioritize discount prices over original price
    const discountedPriceRaw = item?.discountedPrice ?? item?.discountPrice ?? null;
    const discountedPrice = discountedPriceRaw != null ? Number(discountedPriceRaw) : null;
    const originalPrice = item?.originalPrice != null ? Number(item.originalPrice) : (item?.unitPrice != null ? Number(item.unitPrice) : 0);
    const unitPrice = (discountedPrice != null && discountedPrice > 0) ? discountedPrice : originalPrice;
    const quantity = typeof item.quantity === 'number' ? item.quantity : 0;
    return unitPrice * quantity;
  };

  const calculateSelectedTotal = () => {
    return selectedItems.reduce((total, itemId) => {
      const item = items.find((i: any) => i.itemId === itemId);
      return total + (item ? calculateItemTotal(item) : 0);
    }, 0);
  };

  if (!order) {
    return null;
  }

  return (
    <>
      <Dialog 
        open={open} 
        onClose={onClose} 
        maxWidth="lg" 
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          borderBottom: '1px solid #e0e0e0',
          pb: 2
        }}>
          <Box display="flex" alignItems="center" gap={1}>
            <Warning color="error" />
            <Typography variant="h6" fontWeight="bold" color="error">
              Delete Order Items
            </Typography>
          </Box>
          <Button onClick={onClose} startIcon={<Close />}>
            Close
          </Button>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          <Alert severity="warning" sx={{ mb: 3 }}>
            <Typography variant="body2">
              <strong>Warning:</strong> This action cannot be undone. Please select the items you want to delete from order #{order.orderId}.
            </Typography>
          </Alert>

          {/* Order Info */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} gap={2}>
                <Box flex={1}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Customer
                  </Typography>
                  <Typography variant="h6" fontWeight="medium">
                    {order.customerName}
                  </Typography>
                </Box>
                <Box flex={1}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Total Items
                  </Typography>
                  <Typography variant="h6" fontWeight="medium">
                    {items.length} items
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Selection Summary */}
          {selectedItems.length > 0 && (
            <Box mb={2} p={2} bgcolor="#fff3cd" borderRadius={1} border="1px solid #ffeaa7">
              <Typography variant="subtitle2" color="warning.dark" mb={1}>
                Selected for deletion: {selectedItems.length} items
              </Typography>
              <Typography variant="body2" color="warning.dark">
                Total value: {formatPrice(calculateSelectedTotal())}
              </Typography>
            </Box>
          )}

          {/* Items Table */}
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedItems.length === items.length && items.length > 0}
                      indeterminate={selectedItems.length > 0 && selectedItems.length < items.length}
                      onChange={handleSelectAll}
                    />
                  </TableCell>
                  <TableCell>Item</TableCell>
                  <TableCell align="center">Quantity</TableCell>
                  <TableCell align="right">Unit Price</TableCell>
                  <TableCell align="right">Total</TableCell>
                  <TableCell align="center">Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item: any, index: number) => (
                  <TableRow 
                    key={item.itemId || index}
                    sx={{
                      backgroundColor: selectedItems.includes(item.itemId) ? '#fff3cd' : 'inherit'
                    }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedItems.includes(item.itemId)}
                        onChange={() => handleItemToggle(item.itemId)}
                      />
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={2}>
                        <Avatar
                          src={item.imageUrl}
                          alt={item.itemName}
                          sx={{ width: 40, height: 40 }}
                          variant="rounded"
                        />
                        <Box>
                          <Typography fontWeight="medium">
                            {item.itemName}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            SKU: {item.itemCode}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Typography fontWeight="medium">
                        {item.quantity}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography fontWeight="medium">
                          {formatPrice((() => {
                          // Prioritize discount prices over original price
                          const discountedPriceRaw = item?.discountedPrice ?? item?.discountPrice ?? null;
                          const discountedPrice = discountedPriceRaw != null ? Number(discountedPriceRaw) : null;
                          const originalPrice = item?.originalPrice != null ? Number(item.originalPrice) : (item?.unitPrice != null ? Number(item.unitPrice) : 0);
                          return (discountedPrice != null && discountedPrice > 0) ? discountedPrice : originalPrice;
                        })())}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography fontWeight="bold">
                        {formatPrice(calculateItemTotal(item))}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" color="textSecondary">
                        {item.status || order.status}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Instructions */}
          <Box mt={3} p={2} bgcolor="#e3f2fd" borderRadius={1}>
            <Typography variant="body2" color="info.dark">
              <strong>Instructions:</strong> Check the items you want to delete. You can delete individual items or all items at once. 
              The order will be updated to reflect the remaining items.
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3, borderTop: '1px solid #e0e0e0' }}>
          <Button 
            onClick={onClose} 
            variant="outlined"
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteClick} 
            variant="contained" 
            color="error"
            startIcon={<Delete />}
            disabled={selectedItems.length === 0 || isDeleting}
          >
            Delete Selected ({selectedItems.length})
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialog */}
      <Dialog 
        open={showConfirmation} 
        onClose={() => setShowConfirmation(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1,
          color: 'error.main'
        }}>
          <Warning color="error" />
          Confirm Deletion
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" mb={2}>
            Are you sure you want to delete {selectedItems.length} selected item(s)?
          </Typography>
          <Typography variant="body2" color="textSecondary">
            This action cannot be undone. The selected items will be permanently removed from the order.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setShowConfirmation(false)}
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmDelete} 
            variant="contained" 
            color="error"
            startIcon={<CheckCircle />}
            disabled={isDeleting}
          >
            {isDeleting ? 'Deleting...' : 'Confirm Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default OrderDeleteModal; 